import { useQueryClient } from '@tanstack/react-query';
import { PRIORITY_META, type FollowUpSpec, type Priority, type SnoozeInput, type TaskDTO } from '@taskos/shared';
import { useCallback } from 'react';
import { errorMessage, post } from '../../api/client';
import { invalidateTasks } from '../../api/queries';
import { useToast } from '../../components/toast';
import { formatDateTime } from '../../lib/format';
import { useUI } from '../../app/ui';

interface ActionResult {
  task: TaskDTO;
  alreadyDone?: boolean;
}

/**
 * All task mutations used by tiles, rows, detail page, keyboard shortcuts and bulk bar.
 * Every action: server-validated, result toast, Undo where safely reversible, Retry on failure.
 */
export function useTaskActions() {
  const qc = useQueryClient();
  const toast = useToast();
  const ui = useUI();

  const run = useCallback(
    async <T>(label: string, fn: () => Promise<T>, retry?: () => Promise<unknown>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (e) {
        toast.push({ kind: 'error', title: `Could not ${label}`, message: errorMessage(e), action: retry ? { label: 'Retry', onClick: retry } : undefined });
        return undefined;
      } finally {
        invalidateTasks(qc);
      }
    },
    [qc, toast],
  );

  const reopen = useCallback((t: TaskDTO) => run('undo', () => post<ActionResult>(`/api/tasks/${t.id}/reopen`)), [run]);
  const unarchive = useCallback((t: TaskDTO) => run('undo', () => post<ActionResult>(`/api/tasks/${t.id}/unarchive`)), [run]);

  const complete = useCallback(
    async (t: TaskDTO, followUp?: FollowUpSpec): Promise<ActionResult | undefined> => {
      const res = await run('complete the task', () => post<ActionResult>(`/api/tasks/${t.id}/complete`, followUp ? { followUp } : {}), () => complete(t, followUp));
      if (!res) return;
      toast.push({
        kind: 'success',
        title: res.alreadyDone ? 'Already completed' : '✅ Done',
        message: t.title,
        key: `done:${t.id}`,
        actions: res.alreadyDone
          ? []
          : [
              { label: 'Add follow-up', onClick: () => ui.openFollowUp(res.task) },
              { label: 'Undo', onClick: () => reopen(res.task) },
            ],
      });
      return res;
    },
    [run, toast, ui, reopen],
  );

  const cancel = useCallback(
    async (t: TaskDTO, reason?: string): Promise<ActionResult | undefined> => {
      const res = await run('cancel the task', () => post<ActionResult>(`/api/tasks/${t.id}/cancel`, { reason }), () => cancel(t, reason));
      if (res)
        toast.push({ kind: 'success', title: 'Task cancelled', message: t.title, key: `cancel:${t.id}`, action: res.alreadyDone ? undefined : { label: 'Undo', onClick: () => reopen(res.task) } });
      return res;
    },
    [run, toast, reopen],
  );

  const archive = useCallback(
    async (t: TaskDTO): Promise<ActionResult | undefined> => {
      const res = await run('archive the task', () => post<ActionResult>(`/api/tasks/${t.id}/archive`), () => archive(t));
      if (res)
        toast.push({ kind: 'success', title: 'Archived', message: t.title, key: `archive:${t.id}`, action: res.alreadyDone ? undefined : { label: 'Undo', onClick: () => unarchive(res.task) } });
      return res;
    },
    [run, toast, unarchive],
  );

  const snooze = useCallback(
    async (t: TaskDTO, input: SnoozeInput): Promise<ActionResult | undefined> => {
      const res = await run('snooze', () => post<ActionResult>(`/api/tasks/${t.id}/snooze`, input), () => snooze(t, input));
      if (res)
        toast.push({
          kind: 'success',
          title: `⏰ Snoozed until ${formatDateTime(res.task.snoozedUntil)}`,
          message: t.title,
          key: `snooze:${t.id}`,
          action: { label: 'Undo', onClick: () => run('undo snooze', () => post(`/api/tasks/${t.id}/unsnooze`)) },
        });
      return res;
    },
    [run, toast],
  );

  const setPriority = useCallback(
    async (t: TaskDTO, p: Priority): Promise<ActionResult | undefined> => {
      if (t.priority === p) return;
      const prev = t.priority;
      const res = await run('change priority', () => post<ActionResult>(`/api/tasks/${t.id}/priority`, { priority: p }), () => setPriority(t, p));
      if (res)
        toast.push({
          kind: 'success',
          title: `Priority ${PRIORITY_META[prev].label} → ${PRIORITY_META[p].label}`,
          message: t.title,
          key: `prio:${t.id}`,
          action: { label: 'Undo', onClick: () => run('undo', () => post(`/api/tasks/${t.id}/priority`, { priority: prev })) },
        });
      return res;
    },
    [run, toast],
  );

  const assign = useCallback(
    async (t: TaskDTO, userId: string | null, name?: string): Promise<ActionResult | undefined> => {
      const res = await run('assign', () => post<ActionResult>(`/api/tasks/${t.id}/assign`, { assigneeId: userId }), () => assign(t, userId, name));
      if (res) toast.push({ kind: 'success', title: userId ? `Assigned to ${name ?? 'user'}` : 'Unassigned', message: t.title, key: `assign:${t.id}` });
      return res;
    },
    [run, toast],
  );

  return { complete, cancel, archive, unarchive, reopen, snooze, setPriority, assign };
}
